import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export interface KnowledgebaseFormValues {
  title: string;
  keywords: string; // comma-separated; parent converts to/from array
  category: string;
  estimatedMinutes: number;
  helpDocUrl: string;
  solutionNotes: string;
  isActive: boolean;
  reviewStatus: string;
}

interface KnowledgebaseFormProps {
  defaultValues?: KnowledgebaseFormValues;
  onSubmit: (data: KnowledgebaseFormValues) => void;
  isPending: boolean;
}

const CATEGORIES = [
  { value: "website_change", label: "Website Change" },
  { value: "tech_support", label: "Tech Support" },
  { value: "creative_design", label: "Creative Design" },
  { value: "new_project", label: "New Project" },
  { value: "other", label: "Other" },
] as const;

const REVIEW_STATUSES = [
  { value: "active", label: "Active" },
  { value: "pending", label: "Pending" },
  { value: "rejected", label: "Rejected" },
] as const;

export function KnowledgebaseForm({
  defaultValues,
  onSubmit,
  isPending,
}: KnowledgebaseFormProps) {
  const [title, setTitle] = useState(defaultValues?.title ?? "");
  const [keywords, setKeywords] = useState(defaultValues?.keywords ?? "");
  const [category, setCategory] = useState(defaultValues?.category ?? "website_change");
  const [estimatedMinutes, setEstimatedMinutes] = useState(
    defaultValues?.estimatedMinutes ?? 30,
  );
  const [helpDocUrl, setHelpDocUrl] = useState(defaultValues?.helpDocUrl ?? "");
  const [solutionNotes, setSolutionNotes] = useState(
    defaultValues?.solutionNotes ?? "",
  );
  const [isActive, setIsActive] = useState(defaultValues?.isActive ?? true);
  const [reviewStatus, setReviewStatus] = useState(
    defaultValues?.reviewStatus ?? "active",
  );

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    onSubmit({
      title,
      keywords,
      category,
      estimatedMinutes,
      helpDocUrl,
      solutionNotes,
      isActive,
      reviewStatus,
    });
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Title */}
      <div className="space-y-1.5">
        <Label htmlFor="kb-title">Title</Label>
        <Input
          id="kb-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Entry title"
          required
        />
      </div>

      {/* Keywords */}
      <div className="space-y-1.5">
        <Label htmlFor="kb-keywords">Keywords (comma-separated)</Label>
        <Input
          id="kb-keywords"
          value={keywords}
          onChange={(e) => setKeywords(e.target.value)}
          placeholder="e.g. dns, domain, email"
        />
      </div>

      {/* Category */}
      <div className="space-y-1.5">
        <Label>Category</Label>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger>
            <SelectValue placeholder="Select category" />
          </SelectTrigger>
          <SelectContent>
            {CATEGORIES.map((c) => (
              <SelectItem key={c.value} value={c.value}>
                {c.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Estimated Minutes */}
      <div className="space-y-1.5">
        <Label htmlFor="kb-minutes">Estimated Minutes</Label>
        <Input
          id="kb-minutes"
          type="number"
          min={1}
          value={estimatedMinutes}
          onChange={(e) => setEstimatedMinutes(Number(e.target.value))}
        />
      </div>

      {/* Help Doc URL */}
      <div className="space-y-1.5">
        <Label htmlFor="kb-url">Help Doc URL</Label>
        <Input
          id="kb-url"
          value={helpDocUrl}
          onChange={(e) => setHelpDocUrl(e.target.value)}
          placeholder="https://..."
        />
      </div>

      {/* Solution Notes */}
      <div className="space-y-1.5">
        <Label htmlFor="kb-notes">Solution Notes</Label>
        <Textarea
          id="kb-notes"
          value={solutionNotes}
          onChange={(e) => setSolutionNotes(e.target.value)}
          placeholder="Internal notes about this solution..."
          rows={4}
        />
      </div>

      {/* Is Active */}
      <div className="flex items-center gap-3">
        <Switch
          id="kb-active"
          checked={isActive}
          onCheckedChange={setIsActive}
        />
        <Label htmlFor="kb-active">Active</Label>
      </div>

      {/* Review Status */}
      <div className="space-y-1.5">
        <Label>Review Status</Label>
        <Select value={reviewStatus} onValueChange={setReviewStatus}>
          <SelectTrigger>
            <SelectValue placeholder="Select status" />
          </SelectTrigger>
          <SelectContent>
            {REVIEW_STATUSES.map((s) => (
              <SelectItem key={s.value} value={s.value}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Submit */}
      <div className="flex justify-end pt-2">
        <Button type="submit" disabled={isPending || !title.trim()}>
          {isPending ? "Saving..." : "Save Entry"}
        </Button>
      </div>
    </form>
  );
}
